/**
 * Validación y resumen de un archivo de respaldo ICJEME (lado cliente).
 * Alineado con backend `icjeme_io.import_runner._validate_full_restore_payload`.
 * No modifica datos del servidor.
 */

const REQUIRED_LIST_KEYS = ["cells", "members", "guests", "attendance"];

const OPTIONAL_LIST_KEYS = [
  "cell_meeting_attendance",
  "cell_meeting_sessions",
  "cell_discipleship_attendance",
  "cell_quick_weekly_stats",
  "users",
];

const COUNT_LABELS = {
  cells: "Células",
  members: "Miembros",
  guests: "Invitados",
  attendance: "Registros de asistencia",
  cell_meeting_attendance: "Asistencia por célula (reunión)",
  cell_meeting_sessions: "Sesiones de reunión",
  cell_discipleship_attendance: "Asistencia de discipulado",
  cell_quick_weekly_stats: "Estadística rápida semanal",
  users: "Usuarios (solo referencia; no se restauran)",
};

/**
 * @param {unknown} payload
 * @throws {Error}
 */
export function validateIcjemeBackupPayload(payload) {
  if (payload == null || typeof payload !== "object" || Array.isArray(payload)) {
    throw new Error("El archivo no es un respaldo válido de ICJEME (debe ser un objeto JSON).");
  }

  const sc = payload.scope_cell_ids;
  if (sc != null && !(Array.isArray(sc) && sc.length === 0)) {
    throw new Error(
      "Este archivo es un respaldo parcial (ámbito de líder). Solo se puede restaurar un respaldo " +
        "completo de iglesia (descargado desde «Respaldo JSON» en Herramientas Administrativas).",
    );
  }

  for (const key of REQUIRED_LIST_KEYS) {
    if (!(key in payload) || !Array.isArray(payload[key])) {
      throw new Error(
        `El archivo no es un respaldo válido de ICJEME: falta la lista obligatoria «${key}» o no es un arreglo.`,
      );
    }
  }

  for (const key of OPTIONAL_LIST_KEYS) {
    if (key in payload && !Array.isArray(payload[key])) {
      throw new Error(`El archivo no es un respaldo válido: la clave «${key}» debe ser una lista si está presente.`);
    }
  }
}

/**
 * @param {File} file
 * @returns {Promise<{ file: File, payload: object, summary: object }>}
 */
export async function parseAndValidateBackupFile(file) {
  if (!file) {
    throw new Error("No se seleccionó ningún archivo.");
  }
  if (file.size === 0) {
    throw new Error("El archivo está vacío.");
  }
  const maxBytes = 80 * 1024 * 1024;
  if (file.size > maxBytes) {
    throw new Error("El archivo supera el límite de 80 MB.");
  }

  let text;
  try {
    text = await file.text();
  } catch {
    throw new Error("No se pudo leer el archivo seleccionado.");
  }

  text = String(text || "").replace(/^\uFEFF/, "").trim();
  if (!text) {
    throw new Error("El archivo está vacío o no contiene JSON.");
  }

  let payload;
  try {
    payload = JSON.parse(text);
  } catch {
    throw new Error("El archivo no es un JSON válido (está dañado o mal formado).");
  }

  validateIcjemeBackupPayload(payload);

  const counts = {};
  for (const key of [...REQUIRED_LIST_KEYS, ...OPTIONAL_LIST_KEYS]) {
    if (Array.isArray(payload[key])) {
      counts[key] = payload[key].length;
    } else if (REQUIRED_LIST_KEYS.includes(key)) {
      counts[key] = 0;
    }
  }

  const countRows = Object.entries(COUNT_LABELS)
    .filter(([key]) => key in counts)
    .map(([key, label]) => ({
      key,
      label,
      count: counts[key],
    }));

  const summary = {
    fileName: file.name || "sin_nombre.json",
    fileSizeBytes: file.size,
    exportedAt: typeof payload.exported_at === "string" ? payload.exported_at : null,
    schemaVersion: payload.schema_version != null ? String(payload.schema_version) : null,
    exportKind: typeof payload.export_kind === "string" ? payload.export_kind : null,
    countRows,
  };

  return { file, payload, summary };
}

export function formatBackupDateTime(iso) {
  if (!iso) return "No indicada en el archivo";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  try {
    return d.toLocaleString("es-MX", {
      dateStyle: "long",
      timeStyle: "short",
    });
  } catch {
    return String(iso);
  }
}

export function formatFileSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
